const DEVTOOLS_COOKIE = "state_devtools";

/**
 * Determines if Zustand devtools should be enabled for a store.
 *
 * **Default behavior:** ON when `isProd` is `false`, OFF when `true`.
 *
 * **Runtime override:** Set the `state_devtools` cookie to `"1"` (force ON)
 * or `"0"` (force OFF) on any environment, including production.
 *
 * Cookies are used instead of feature flags because stores initialize at
 * module-load time (before React render / authentication), and feature flags
 * require an async network call with auth context. `document.cookie` is
 * synchronous and available immediately. Pasting the following into the browser
 * console will enable devtools for 2 hours:
 *
 * ```js
 * document.cookie = "state_devtools=1; path=/; max-age=7200";
 * location.reload();
 * ```
 *
 * @param isProd - Whether the app is running in production. Passed by the
 *   consuming app so this package has no dependency on Vite or any bundler.
 */
export function isDevtoolsEnabled(isProd: boolean): boolean {
    try {
        const cookieValue = document.cookie
            .split("; ")
            .find((c) => c.startsWith(`${DEVTOOLS_COOKIE}=`))
            ?.split("=")[1];

        if (cookieValue === "1") return true;
        if (cookieValue === "0") return false;
    } catch {
        // Non-browser environment (SSR, tests) — fall through to default
    }

    return !isProd;
}
